/**
 * Support module for API Repository.
 * This file provides shared implementation details for its surrounding feature or platform area.
 */
import type {
  AgencyProfileConfiguration,
  AgencyRecord,
  AgencyUpsertPayload as AgencyUpsert,
} from "../../../features/agencies/agencies.types";
import type {
  AgentApprovalRequestRecord,
  AgentTargetAnalyticsResult,
  AgentTargetRecord,
  AgentRecord,
  TaskActivity,
  TaskRecord,
} from "../../../features/agents/agents.types";
import type {
  PosRecord,
  PosStockDetails,
  PosStockEntry,
  PosStockTransactionPayload,
  PosUpsertPayload,
  ProspectRecord,
  ProspectStockTransactionPayload,
  ProspectUpsertPayload,
} from "../../../features/pos/pos.types";
import type {
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload,
} from "../../../features/supervisors/supervisors.types";

export type {
  AgencyProfileConfiguration,
  AgencyRecord,
  AgencyUpsertPayload as AgencyUpsert,
} from "../../../features/agencies/agencies.types";
export type {
  AgentApprovalRequestRecord,
  AgentTargetAnalyticsResult,
  AgentTargetRecord,
  AgentRecord,
  TaskActivity,
  TaskRecord,
} from "../../../features/agents/agents.types";
export type {
  PosRecord,
  PosStockDetails,
  PosStockEntry,
  PosStockTransactionPayload,
  PosUpsertPayload,
  ProspectRecord,
  ProspectStockTransactionPayload,
  ProspectUpsertPayload,
} from "../../../features/pos/pos.types";
export type {
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload,
} from "../../../features/supervisors/supervisors.types";

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface SupervisorSignupRequestPayload extends AuthCredentials {
  fullName: string;
  phone: string;
  preferredLanguage?: UserLanguage;
}

export type UserLanguage = "en" | "fr";

export interface AuthUserPhone {
  dialCode?: number;
  iso2?: string;
  country?: string;
  number?: string;
}

export interface AuthUserAddress {
  formattedAddress?: string;
  streetAddress?: string;
  locality?: string;
  subLocality?: string;
  region?: string;
  country?: string;
  postalCode?: string;
  area?: string;
  geo?: { type?: string; coordinates?: [number, number] };
}

export interface UpdateProfilePayload {
  fullName?: string;
  phone?: AuthUserPhone;
  address?: AuthUserAddress;
  profilePhoto?: string | null;
  preferredLanguage?: string;
  preferredTheme?: string;
}

export interface UpdatePreferencesPayload {
  preferredLanguage?: string;
  preferredTheme?: string;
}

export interface AssignedAgencySummary {
  id: string;
  name: string;
  city?: string;
  area?: string;
}

export type UserAccountStatus =
  | "pending_approval"
  | "active"
  | "rejected"
  | "disabled"
  | "locked"
  | "inactive";
export type UserApprovalStatus =
  | "not_required"
  | "pending"
  | "approved"
  | "rejected";

export interface ApiUserSummary {
  id: string;
  fullName: string;
  email: string;
  role: { id: string; name: string; permissions: string[] };
  accountStatus: UserAccountStatus;
  approvalStatus: UserApprovalStatus;
  contact?: { phone?: AuthUserPhone };
  address?: AuthUserAddress;
  assignedAgencyIds?: string[];
  assignedPosIds?: string[];
}

export interface UsersListParams {
  page?: number;
  limit?: number;
  search?: string;
  roleId?: string;
  accountStatus?: string;
  approvalStatus?: string;
}

export interface UsersListResult {
  items: ApiUserSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface DashboardActiveSummary {
  activeAgents: number;
  btlUsers: number;
  logisticsUsers: number;
  supervisors: number;
}

export interface DashboardCentralInventoryItem {
  productId: string;
  productName: string;
  quantity: number;
  threshold: number;
}

export interface DashboardAgencyInventoryItem {
  product: {
    id: string;
    name: string;
  } | null;
  quantity: number;
  threshold: number;
}

export type DashboardTargetPeriod = 'daily' | 'weekly' | 'monthly';

export interface SupervisorDashboardSummaryQuery {
  agentAuthUserId?: string;
  targetPeriod?: DashboardTargetPeriod;
  targetDate?: string;
}

export interface DashboardMetricValue {
  value: number;
}

export interface DashboardRecentAgentTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate: string;
  progressPercent: number;
  latestActivityAt?: string;
  activityCount: number;
  assignedPosCount: number;
}

export interface DashboardAgentStockItem {
  id: string;
  quantity: number;
  unit: string;
  category: { id: string; name: string };
  product: { id: string; name: string };
  variant: { id: string; name: string; unit: string };
  updatedAt: string;
}

export interface DashboardAgentTargetProgressRow {
  id: string;
  userType: 'AGENT' | 'BTL';
  userId: string;
  userName?: string;
  agencyId: string;
  agencyName?: string;
  targetType: 'stock' | 'float-sale' | 'recharge-pos' | 'customer-onboard';
  categoryId?: string | null;
  categoryName?: string;
  productId?: string | null;
  productName?: string;
  dailyTarget: number;
  weeklyTarget: number;
  monthlyTarget: number;
  period: DashboardTargetPeriod;
  periodStart: string;
  periodEnd: string;
  targetValue: number;
  achievedValue: number;
  remainingValue: number;
  achievementPercent: number;
  status: 'not-started' | 'in-progress' | 'met' | string;
  notes: string;
  createdByAuthUserId: string;
  updatedByAuthUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardAgentPerformance {
  agent: {
    id: string;
    name?: string;
  };
  tasks: {
    total: DashboardMetricValue;
    open: DashboardMetricValue;
    inProgress: DashboardMetricValue;
    completed: DashboardMetricValue;
    recent: DashboardRecentAgentTask[];
  };
  stockItems: DashboardAgentStockItem[];
  targets: DashboardAgentTargetProgressRow[];
  achievements: {
    assignedTargetValue: DashboardMetricValue;
    achievedValue: DashboardMetricValue;
    remainingValue: DashboardMetricValue;
    averageAchievementPercent: DashboardMetricValue;
  };
}

export interface DashboardSummary {
  activePos: number;
  activeProspects: number;
  activeAgencies: number;
  openAgentTasks: number;
  openBtlCampaigns: number;
  openLogisticsTasks: number;
  totalStockCount: number;
  centralInventory: DashboardCentralInventoryItem[];
}

export interface DashboardPdfExport {
  fileName: string;
  mimeType: string;
  base64: string;
}

export interface InventoryCatalogExcelExport {
  fileName: string;
  mimeType: string;
  base64: string;
}

export interface TargetPerformanceAnalyticsDocumentExport {
  fileName: string;
  mimeType: string;
  base64: string;
}

export interface SupervisorDashboardSummary {
  activePos: number;
  activeProspects: number;
  openAgentTasks: number;
  openBtlCampaigns: number;
  openLogisticsTasks: number;
  agencyInventory: DashboardAgencyInventoryItem[];
  supervisorWorkforce: {
    agency: { id: string; name: string } | null;
    activeAgents: number;
    activeLogisticsUsers: number;
    activeBtlUsers: number;
  };
  supervisorPendingWork: {
    agentTasks: number;
    logisticsTasks: number;
    btlActivities: number;
  };
  supervisorPendingApprovals: {
    total: number;
    agentTaskApprovals: number;
    logisticsTaskApprovals: number;
    btlSubmissionApprovals: number;
    fuelRequestApprovals: number;
    maintenanceRequestApprovals: number;
    routeChangeRequestApprovals: number;
    prospectApprovals: number;
  };
  agentPerformance?: DashboardAgentPerformance;
}

export interface PosListQuery {
  page?: number;
  limit?: number;
  search?: string;
  profile?: string;
  posType?: string;
  status?: string;
  operationalStatus?: string;
  stockStatus?: string;
  assignedAgent?: string;
  assignedAgentAuthUserId?: string;
  agency?: string;
  agencyId?: string;
  // Backend filters POS by the agency's primary id; the list sends the selected agency here.
  primaryAgencyId?: string;
}

export interface ProspectListQuery {
  page?: number;
  limit?: number;
  search?: string;
  profile?: string;
  posType?: string;
  creationStatus?: string;
  status?: string;
  operationalStatus?: string;
  assignedAgent?: string;
  assignedAgentAuthUserId?: string;
  agency?: string;
  // Backend filters prospects by the agency's primary id; the list sends the selected agency here.
  primaryAgencyId?: string;
}

export interface ProspectApprovalPayload {
  approvalNote: string;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface UserAddressPayload {
  formattedAddress?: string;
  streetAddress?: string;
  locality?: string;
  subLocality?: string;
  region?: string;
  country?: string;
  postalCode?: string;
  area?: string;
  geo?: { type: "Point"; coordinates: [number, number] };
}

export interface UserProfile {
  agentType?: string;
  dealerId?: string;
  joinedAt?: string;
  assignedAgency?: {
    agencyId?: string;
    agencyName?: string;
    city?: string;
    area?: string;
  };
  assignedPos?: unknown[];
  guarantorContact1?: { name?: string; phoneNumber?: string };
  guarantorContact2?: { name?: string; phoneNumber?: string };
  [key: string]: unknown;
}

export interface LogisticsProfile {
  employeeCode?: string;
  baseHub?: string;
  shift?: string;
  assignedVehicleId?: string;
  assignedVehicleLabel?: string;
  assignedSupervisorId?: string | null;
  joinedAt?: string;
}

export interface AdminProfile {
  userTypeLabel?: string;
}

export interface CreateUserPayload {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email: string;
  password: string;
  preferredLanguage?: string;
  employeeCode?: string;
  accountStatus?: string;
  roleCode?: string;
  roleId?: string;
  contact?: { email?: string; phone?: AuthUserPhone };
  address?: UserAddressPayload;
  assignedSupervisorId?: string | null;
  assignedAgencyId?: string | null;
  assignedAgencyIds?: string[];
  assignedPosIds?: string[];
  profile?: {
    agent?: UserProfile;
    logistics?: LogisticsProfile;
    admin?: AdminProfile;
  };
}

export interface UpdateUserStatusPayload {
  accountStatus: UserAccountStatus;
}

export interface UpdateUserProfilePayload {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  preferredLanguage?: string;
  employeeCode?: string;
  roleId?: string;
  phone?: AuthUserPhone;
  contact?: { email?: string; phone?: AuthUserPhone };
  address?: UserAddressPayload;
  assignedSupervisorId?: string | null;
  assignedAgencyId?: string | null;
  assignedAgencyIds?: string[];
  assignedPosIds?: string[];
  accountStatus?: string;
  documents?: {
    documentType?: string;
    documentNumber?: string;
    fileName?: string;
    fileUrl?: string;
  }[];
  profile?: {
    agent?: UserProfile;
    logistics?: LogisticsProfile;
    admin?: AdminProfile;
  };
}

export interface AuthUser {
  id?: string;
  authUserId?: string;
  email: string;
  role: "admin" | "supervisor" | "agent" | "btl" | "logistics" | string;
  roleCode?: string;
  name: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  phoneDetails?: AuthUserPhone;
  addressDetails?: AuthUserAddress;
  avatar?: string;
  language?: UserLanguage;
  preferredLanguage?: string;
  preferredTheme?: "light" | "dark";
  roles?: string[];
  customRbacRoles?: string[];
  permissions?: string[];
  status?: "active" | "inactive" | "pending";
  userType?: string;
  agency?: string;
  supervisor?: string;
  assignedAgencyId?: string | null;
  assignedAgencyIds?: string[];
  assignedAgency?: AssignedAgencySummary | null;
  assignedSupervisorId?: string | null;
}

export interface AgentListQuery {
  page?: number;
  limit?: number;
  search?: string;
  accountStatus?: string;
  approvalStatus?: string;
  agentType?: string;
  agency?: string;
  assignedAgencyId?: string;
}

export interface AgencyDirectSalesLogQuery {
  agencyId?: string;
  page?: number;
  limit?: number;
  agentAuthUserId?: string;
  fromDate?: string;
  toDate?: string;
}

export interface AgencyListQuery {
  page?: number;
  limit?: number;
  search?: string;
  profile?: string;
  status?: string;
  city?: string;
}

export interface TaskListQuery {
  page?: number;
  limit?: number;
  search?: string;
  assineeAuthUserId?: number;
  assigneeUserType?: string;
  agencyId?: string | number;
  status?: string;
  priority?: string;
  fromDate?: Date;
  toDate?: Date;
}

export interface TaskActivityListQuery {
  page?: number;
  limit?: number;
  taskId?: string;
}

export interface CreateTaskPayload {
  title: string;
  description?: string;
  assigneeAuthUserId: string;
  assigneeUserType: string;
  assignedAgencyId?: string;
  assignedAgencyName?: string;
  assignedPosIds?: string[];
  dueDate?: string;
  priority?: string;
  status?: string;
  instructions?: string;
  stockToCarry?: Array<{
    categoryId: string;
    products: Array<{
      productId: string;
      variants: Array<{
        variantId: string;
        quantity: number;
      }>;
    }>;
  }>;
  cashToCarry: number;
}

export interface UpdateTaskPayload extends CreateTaskPayload {}

export interface AgentTaskReviewPayload {
  decision: "approve" | "reject";
  reviewerNotes?: string;
}

export interface AgentTaskReviewResult {
  id: string;
  status?: string;
  approval?: { status?: string };
}

export interface LogisticsTaskLegPayload {
  id?: string;
  legOrder: number;
  movementType: "STOCK" | "CASH" | "PEOPLE";
  fromLocationId: string;
  fromLocationType: "CENTRAL" | "AGENCY" | "FIELD";
  toLocationId: string;
  toLocationType: "CENTRAL" | "AGENCY" | "FIELD";
  stockCargo?: Array<{
    variantId?: string;
    productId: string;
    categoryId: string;
    variantName?: string;
    unit: string;
    qtyAssigned: number;
    qtyPickedUp: number;
    qtyDelivered: number;
    qtyLost: number;
    qtyDamaged: number;
    qtyReturning: number;
  }>;
  cashCargo?: {
    amountAssigned: number;
    note?: string;
  };
  peopleCargo?: {
    passengerIds: string[];
    passengerCount: number;
  };
  note?: string;
}

export interface LogisticsTaskUpsertPayload {
  title: string;
  assignedToAuthUserId?: string;
  vehicleId?: string;
  parallelLegs?: boolean;
  scheduledDate: string;
  note?: string;
  legs: LogisticsTaskLegPayload[];
}

export interface CentralReference {
  id: string;
  name: string;
}

export interface BtlUserApiRecord {
  id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  email: string;
  accountStatus: string;
  contactPhoneDisplay?: string;
  contact?: {
    phone?: {
      dialCode?: number;
      iso2?: string;
      country?: string;
      number?: string;
    };
  };
  assignedAgency?: AssignedAgencySummary | null;
  assignedSupervisor?: { id: string; fullName: string; email?: string } | null;
  btlProfile?: {
    employeeCode?: string;
    designation?: string;
    joinedAt?: string;
    lastActivityDate?: string;
  };
  campaignSummary?: {
    totalCampaigns?: number;
    upcomingCampaigns?: number;
    activeCampaigns?: number;
    completedCampaigns?: number;
    totalEntries?: number;
  };
  address?: {
    formattedAddress?: string;
    streetAddress?: string;
    locality?: string;
    subLocality?: string;
    region?: string;
    country?: string;
    postalCode?: string;
    area?: string;
    geo?: { coordinates?: [number, number] };
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface BtlDashboardSummary {
  totalBtlUsers: {
    value: number;
    secondaryValue: number;
    activeUsers?: number;
  };
  activeCampaigns: {
    value: number;
    secondaryValue: number;
    totalCampaigns?: number;
  };
  pendingActivityReviews: {
    value: number;
    secondaryValue: number;
    totalProofs?: number;
  };
  targetAchievement: { achieved: number; target: number; percent: number };
}

export interface BtlCampaignListQuery {
  page?: number;
  limit?: number;
  search?: string;
  campaignType?: string;
  status?: string;
  assignedUser?: string;
}

export interface BtlCampaignListItem {
  id: string;
  campaignName?: string;
  campaignType: string;
  location: string;
  startDate: string;
  endDate: string;
  totalTasks: number;
  progressPercent: number | null;
  status: string;
  targetStockToSell: number;
  targetPeopleToOnboard: number;
  stockSold: number;
  peopleOnboarded: number;
  assignedUsersCount: number;
  assignedUserNames: string[];
  submissions: BtlCampaignSubmissionListItem[];
}

export interface BtlCampaignSubmissionListItem {
  id: string;
  btlUserName: string;
  date: string;
  taskCompleted: string;
  status: string;
  unitsDistributed: number;
  consumerInteractions: number;
  notes: string;
  itemsSummary: string;
  evidencePhotos: string[];
}

export interface BtlCampaignInventoryVariantPayload {
  variantId: string;
  quantity: number;
}

export interface BtlCampaignInventoryProductPayload {
  productId: string;
  variants: BtlCampaignInventoryVariantPayload[];
}

export interface BtlCampaignInventoryCategoryPayload {
  categoryId: string;
  products: BtlCampaignInventoryProductPayload[];
}

export interface BtlCampaignUpsertPayload {
  campaignName: string;
  campaignType: string;
  agencyId?: string;
  agency?: string;
  purpose?: string;
  durationDays: number;
  startDate: string;
  endDate: string;
  targetStockToSell: number;
  targetPeopleToOnboard: number;
  checklistTargets: string[];
  assignedBtlUserIds: string[];
  items?: BtlCampaignInventoryCategoryPayload[];
  address: UserAddressPayload;
  status?: string;
}

export interface BtlCampaignInventoryItemApiRecord {
  categoryId: string;
  categoryName: string;
  productId: string;
  productName: string;
  variantId: string;
  variantName: string;
  productKind: string;
  unit: string;
  quantity: number;
}

export interface BtlCampaignApiRecord {
  id: string;
  campaignName: string;
  campaignType: string;
  agencyId?: string;
  agency: string;
  purpose: string;
  address?: UserAddressPayload;
  durationDays: number;
  startDate: string;
  endDate: string;
  targetStockToSell: number;
  targetPeopleToOnboard: number;
  checklistTargets: string[];
  assignedBtlUserIds: string[];
  items: BtlCampaignInventoryItemApiRecord[];
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LogisticsDashboardSummary {
  totalLogisticsUsers: { value: number; secondaryValue: number };
  activeUsers: { value: number; secondaryValue: number };
  usersOnRoute: { value: number; secondaryValue: number };
  pendingApprovals: { value: number; secondaryValue: number };
  assignedVehicles: { value: number; secondaryValue: number };
  logisticsTasks: {
    totalTasks: number;
    openTasks: number;
    pendingApproval: number;
    closedApprovalPending: number;
  };
  fuelManagement: {
    fuelEntries: number;
    pendingApproval: number;
    approvedEntries: number;
    fuelCardUsage: number;
    linkedTaskEntries: number;
  };
  vehicleManagement: {
    allVehicles: number;
    activeVehicles: number;
    assignedVehicles: number;
    vehiclesWithDocuments: number;
    vehiclesWithoutDocuments: number;
  };
  vehicleMaintenance: {
    maintenanceRequests: number;
    pendingReview: number;
    highPriority: number;
    completedRepairs: number;
    vehicleLinkedRequests: number;
    distinctVehicles: number;
  };
}

export interface LogisticsTaskListQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  movementType?: string;
}

export interface LogisticsTaskListItem {
  id: string;
  title: string;
  assignedToAuthUserId?: string;
  assignedToName?: string;
  vehicleId?: string | null;
  vehicleName?: string;
  createdByAuthUserId?: string;
  createdByName?: string;
  status?: string;
  approval?: { status?: string };
  parallelLegs?: boolean;
  scheduledDate?: string;
  note?: string;
  legCount?: number;
  completedLegCount?: number;
  lastActivityAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LogisticsTaskDetailLegApiRecord {
  id?: string;
  taskLegId?: string;
  routeLegId?: string;
  taskId?: string;
  legOrder?: number;
  movementType?: string;
  fromLocationId?: string;
  fromLocationType?: string;
  fromLocationName?: string;
  fromLocationLabel?: string;
  toLocationId?: string;
  toLocationType?: string;
  toLocationName?: string;
  toLocationLabel?: string;
  stockCargo?: Array<{
    variantId?: string;
    productId?: string;
    categoryId?: string;
    variantName?: string;
    productName?: string;
    categoryName?: string;
    unit?: string;
    qtyAssigned?: number;
    qtyPickedUp?: number;
    qtyDelivered?: number;
    qtyLost?: number;
    qtyDamaged?: number;
    qtyReturning?: number;
  }>;
  cashCargo?: {
    amountAssigned?: number;
    note?: string;
  };
  peopleCargo?: {
    passengerType?: string;
    passengerIds?: string[];
    passengerCount?: number;
  };
  note?: string;
  status?: string;
}

export interface LogisticsTaskDetailApiRecord extends LogisticsTaskListItem {
  legs: LogisticsTaskDetailLegApiRecord[];
}

export interface LogisticsUserApiRecord {
  id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  email: string;
  roleCode?: string;
  role?: {
    id: string;
    name: string;
    roleType?: string;
    code?: string;
    userType?: string;
    permissions?: string[];
    isActive?: boolean;
  };
  accountStatus: string;
  approvalStatus?: string;
  preferredLanguage?: string;
  preferredTheme?: string;
  contactPhoneDisplay?: string;
  city?: string;
  area?: string;
  contact?: {
    email?: string;
    phone?: {
      dialCode?: number;
      iso2?: string;
      country?: string;
      number?: string;
    };
  };
  address?: {
    formattedAddress?: string;
    streetAddress?: string;
    locality?: string;
    subLocality?: string;
    region?: string;
    country?: string;
    postalCode?: string;
    area?: string;
    geo?: { type?: string; coordinates?: [number, number] };
  };
  documents?: {
    documentType?: string;
    documentNumber?: string;
    fileName?: string;
    fileUrl?: string;
  }[];
  assignedSupervisorId?: string | null;
  assignedAgencyId?: string | null;
  assignedPosIds?: string[];
  assignedAgency?: AssignedAgencySummary | null;
  assignedSupervisor?: { id: string; fullName: string; email?: string } | null;
  logisticsProfile?: {
    employeeCode?: string;
    baseHub?: string;
    shift?: string;
    assignedVehicle?: string | null;
    assignedVehicleLabel?: string;
    joinedAt?: string;
  };
  profile?: { logistics?: LogisticsProfile };
  taskSummary?: {
    total?: number;
    open?: number;
    inProgress?: number;
    completed?: number;
    stockMovementTasks?: number;
    peopleMovementTasks?: number;
    cashMovementTasks?: number;
    mixedResourceTasks?: number;
    totalActivityEntries?: number;
  };
  activityEntries?: unknown[];
  weeklyAnalysis?: string;
  monthlyAnalysis?: string;
  monthToDateAnalysis?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleApiRecord {
  id: string;
  registrationNumber: string;
  make: string;
  modelName: string;
  vehicleName?: string;
  year?: number;
  type?: string;
  capacity?: number;
  capacityUnit?: string;
  assignedToAuthUserId?: string | null;
  currentAgencyId?: string | null;
  status?: string;
  fuelType?: string;
  isActive?: boolean;
  documents?: Array<{
    imageUris?: string[];
    expiryDate?: string;
    category?: string;
    categoryName?: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateVehicleDocumentPayload {
  imageUris: string[];
  expiryDate: string;
  category: string;
  categoryName?: string;
}

export interface CreateVehicleDocumentStandalonePayload {
  vehicleId: string;
  imageUris: string[];
  expiryDate: string;
  category: string;
  categoryName?: string;
}

export interface VehicleDocumentApiRecord {
  id?: string;
  vehicleId: string;
  imageUris: string[];
  expiryDate: string;
  category: string;
  categoryName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateVehicleDocumentPayload {
  vehicleId: string;
  imageUris: string[];
  expiryDate: string;
  category: string;
  categoryName?: string;
}

export interface VehicleDocumentListQuery {
  page?: number;
  limit?: number;
  vehicleId?: string;
  category?: string;
  expiryFrom?: string;
  expiryTo?: string;
}

export interface CreateVehiclePayload {
  registrationNumber: string;
  modelName: string;
  vehicleName: string;
  type: string;
  fuelType: string;
  status: string;
  isActive: boolean;
  assignedToAuthUserId?: string;
  currentAgencyId?: string;
  documents?: CreateVehicleDocumentPayload[];
}

export interface UpdateVehiclePayload {
  registrationNumber?: string;
  modelName?: string;
  vehicleName?: string;
  type?: string;
  fuelType?: string;
  status?: string;
  isActive?: boolean;
  assignedToAuthUserId?: string;
  currentAgencyId?: string;
  documents?: CreateVehicleDocumentPayload[];
}

export interface MaintenanceTypeRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface MaintenanceRequestListQuery {
  page?: number;
  limit?: number;
  search?: string;
  vehicleId?: string;
  raisedByAuthUserId?: string;
  status?: string;
  priority?: string;
  maintenanceType?: string;
}

export interface CreateMaintenanceRequestPayload {
  logisticsUserId: string;
  vehicleId: string;
  maintenanceTypeId: string;
  issueDescription: string;
  urgency: string;
  status: string;
  cost?: number;
  evidencePhotoUris?: string[];
}

export interface UpdateMaintenanceRequestPayload {
  logisticsUserId: string;
  vehicleId: string;
  maintenanceTypeId: string;
  issueDescription: string;
  urgency: string;
  cost?: number;
  evidencePhotoUris?: string[];
}

export interface MaintenanceRequestApiRecord {
  id: string;
  vehicleId?: string;
  vehicleName?: string;
  maintenanceTypeId?: string;
  maintenanceTypeName?: string;
  issueDescription?: string;
  urgency?: string;
  cost?: number;
  status?: string;
  submittedAt?: string;
  raisedByAuthUserId?: string;
  raisedByName?: string;
  evidencePhotoUris?: string[];
}

export interface FuelRequestApiRecord {
  id: string;
  logisticsUserId?: string;
  logisticsUserName?: string;
  vehicleId?: string;
  vehicleName?: string;
  fuelStationName?: string;
  fuelStationLocation?: {
    formattedAddress?: string;
    streetAddress?: string;
    locality?: string;
    region?: string;
    country?: string;
    postalCode?: string;
    area?: string;
  };
  date?: string;
  odometerReadingKm?: number;
  amountFilledLitres?: number;
  totalCost?: number;
  fuelCardUsed?: string;
  receiptPhotoUri?: string;
  receiptUploaded?: boolean;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FuelRequestListQuery {
  page?: number;
  limit?: number;
  search?: string;
  logisticsUserId?: string;
  vehicleId?: string;
  status?: string;
}

export interface FuelStationLocationPayload {
  formattedAddress?: string;
  streetAddress?: string;
  locality?: string;
  subLocality?: string;
  region?: string;
  country?: string;
  postalCode?: string;
  area?: string;
  geo?: { type: "Point"; coordinates: [number, number] };
}

export interface CreateFuelRequestPayload {
  logisticsUserId: string;
  vehicleId: string;
  date: string;
  odometerReadingKm: number;
  litresFilled: number;
  totalCost: number;
  fuelCardUsed: string;
  stationName: string;
  status?: string;
  stationLocation?: FuelStationLocationPayload;
  receiptPhotoUri?: string;
}

export interface UpdateFuelRequestPayload {
  vehicleId?: string;
  date?: string;
  odometerReadingKm?: number;
  litresFilled?: number;
  totalCost?: number;
  fuelCardUsed?: string;
  stationName?: string;
  stationLocation?: FuelStationLocationPayload;
  receiptPhotoUri?: string;
}

export interface InventoryCategory {
  id: string;
  name: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryProduct {
  id: string;
  categoryId: string;
  name: string;
  threshold: number;
  isCashProduct: boolean;
  requiresVariant: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryVariant {
  id: string;
  productId: string;
  name: string;
  sellingPrice?: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AgentInventoryStockVariant {
  id: string;
  quantity: number;
  unit: string;
  isLow: boolean;
  costPrice?: number;
  updatedAt: string;
  variant: {
    id: string;
    name: string;
  };
}

export interface AgentInventoryStockProductGroup {
  product: {
    id: string;
    name: string;
    threshold: number;
  };
  variants: AgentInventoryStockVariant[];
}

export interface AgentInventoryStockCategoryGroup {
  category: {
    id: string;
    name: string;
  };
  products: AgentInventoryStockProductGroup[];
}

export type CashOwnerType = "central" | "agency" | "agent" | "pos" | "prospect" | "btl" | "logistics";

export type CashMovementDirection = "credit" | "debit" | "neutral" | "in" | "out";

export type CashMovementType =
  | "AGENCY_TO_AGENT"
  | "AGENT_TO_AGENCY"
  | "AGENT_TO_POS"
  | "POS_COLLECTION"
  | "OPENING_BALANCE"
  | "MANUAL_ADJUSTMENT"
  | "BANK_DEPOSIT"
  | "BTL_FLOAT_ISSUED"
  | "BTL_FLOAT_RETURNED"
  | "LOGISTICS_CASH_TRANSFER";

export interface CashOwnerRef {
  ownerType: CashOwnerType;
  ownerId: string;
  ownerName?: string;
}

export interface CashBalance extends CashOwnerRef {
  balance: number;
  updatedAt?: string;
}

export interface CashMovement {
  id: string;
  movementType: CashMovementType | string;
  amount: number;
  direction: CashMovementDirection | string;
  source?: CashOwnerRef;
  destination?: CashOwnerRef;
  note?: string;
  referenceId?: string;
  createdAt?: string;
  createdByName?: string;
  performedByName?: string;
}

export interface CashMovementPayload {
  movementType: CashMovementType;
  amount: number;
  ownerType?: CashOwnerType;
  ownerId?: string;
  direction?: CashMovementDirection;
  sourceOwnerType?: CashOwnerType;
  sourceOwnerId?: string;
  destinationOwnerType?: CashOwnerType;
  destinationOwnerId?: string;
  note?: string | null;
  referenceType?: string;
  referenceId?: string;
  date?: string;
}

export interface CashBalanceListQuery {
  ownerType?: CashOwnerType;
  ownerId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CashMovementListQuery extends CashBalanceListQuery {
  movementType?: CashMovementType | string;
  direction?: CashMovementDirection | string;
  fromDate?: string;
  toDate?: string;
}

export interface AgentInventoryTransactionVariant {
  variant: {
    id: string;
    name: string;
  };
  unit: string;
  quantity: number;
  costPrice?: number;
}

export interface AgentInventoryTransactionProductGroup {
  product: {
    id: string;
    name: string;
  };
  variants: AgentInventoryTransactionVariant[];
}

export interface AgentInventoryTransactionCategoryGroup {
  category: {
    id: string;
    name: string;
  };
  products: AgentInventoryTransactionProductGroup[];
}

export interface AgentInventoryTransactionRecord {
  id: string;
  agencyId: string;
  agencyName: string;
  agentAuthUserId: string;
  posId: string | null;
  taskId: string | null;
  transactionType: string;
  items: AgentInventoryTransactionCategoryGroup[];
  performedByAuthUserId: string;
  performedByName: string;
  status: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgencyInventoryStockVariant {
  id: string;
  quantity: number;
  unit: string;
  variant: {
    id: string;
    name: string;
    unit: string;
  };
}

export interface AgencyInventoryStockProductGroup {
  product: {
    id: string;
    name: string;
    threshold: number;
  };
  totalQuantity: number;
  status: string;
  isLow: boolean;
  variants: AgencyInventoryStockVariant[];
}

export interface AgencyInventoryStockCategoryGroup {
  category: {
    id: string;
    name: string;
  };
  products: AgencyInventoryStockProductGroup[];
}

export interface AgencyInventoryTransactionRecord {
  id: string;
  agencyId: string;
  fromAgencyId: string | null;
  variantId: string;
  productId: string;
  categoryId: string;
  variantName: string;
  productName: string;
  categoryName: string;
  unit: string;
  transactionType: string;
  quantity: number;
  performedByAuthUserId: string;
  performedByName: string;
  logisticsTaskId: string | null;
  status: string;
  note: string;
  date: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgencyDirectSalesLogVariant {
  variant: {
    id: string;
    name: string;
  };
  productKind: string;
  unit: string;
  quantity: number;
  sellingPrice: number;
  lineTotalAmount: number;
  productReference?: string;
}

export interface AgencyDirectSalesLogProductGroup {
  product: {
    id: string;
    name: string;
  };
  variants: AgencyDirectSalesLogVariant[];
}

export interface AgencyDirectSalesLogCategoryGroup {
  category: {
    id: string;
    name: string;
  };
  products: AgencyDirectSalesLogProductGroup[];
}

export interface AgencyDirectSalesLogRecord {
  id: string;
  agencyId: string;
  agentAuthUserId: string;
  posId: string | null;
  customerName: string;
  customerMobileNumber: string;
  items: AgencyDirectSalesLogCategoryGroup[];
  totalAmount: number;
  note: string;
  performedByAuthUserId: string;
  performedByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CentralStockOverviewFilters {
  categoryId: string | null;
  productId: string | null;
}

export interface CentralStockOverviewCardSummary {
  totalStock: number;
  pending: number;
  inTransit: number;
}

export interface CentralStockOverviewRow {
  stockId: string;
  category: {
    id: string;
    name: string;
  };
  product: {
    id: string;
    name: string;
    threshold: number;
  };
  variant: {
    id: string;
    name: string;
    unit: string;
  };
  unit: string;
  onHandQty: number;
  pendingQty: number;
  inTransitQty: number;
  totalQty: number;
  note?: string;
  productReference?: string;
  isLow: boolean;
  lastUpdatedAt: string;
}

export interface CentralStockOverviewData {
  central: {
    id: string;
    name: string;
  };
  filters: CentralStockOverviewFilters;
  cards: CentralStockOverviewCardSummary;
  rows: CentralStockOverviewRow[];
  page?: number;
  limit?: number;
  total?: number;
}

export interface CentralStockListRow {
  id: string;
  category: {
    id: string;
    name: string;
  };
  product: {
    id: string;
    name: string;
    threshold?: number;
  };
  variant: {
    id: string;
    name: string;
    unit?: string;
  };
  unit: string;
  quantity: number;
  totalQuantity?: number;
  status?: string;
  isLow: boolean;
  date: string;
  updatedAt: string;
  note?: string;
  productReference?: string;
  enteredBy?: string;
  createdByName?: string;
}

export interface CentralTransferListRow {
  id: string;
  transferCode: string;
  type: string;
  source: string;
  destination: string;
  initiatedBy: string;
  logisticsUser: string;
  requestDate: string;
  category: string;
  product: string;
  variant: string;
  reservedQty: number;
  status: "pending" | "in-transit" | "delivered" | string;
  deliveryStatus: string;
  approvals: string;
  adminOnlyFlow: string;
  lifecycle: string;
  note: string;
  createdAt: string;
}

export interface CentralBankingNoteRow {
  id: string;
  bankingNote: string;
  date: string;
  enteredBy: string;
  referenceNumber: string;
}

export interface AgencyReferenceOption {
  id: string;
  name: string;
}

export interface StockThresholdRow {
  id: string;
  institutionType: string;
  referenceId: string;
  referenceName: string;
  categoryId: string;
  category: string;
  productId: string;
  product: string;
  quantity: number;
  updatedAt: string;
  createdAt: string;
}

export interface CreateStockThresholdPayload {
  institutionType: "agency" | "central";
  referenceId: string;
  categoryId: string;
  productId: string;
  quantity: number;
}

export interface StockThresholdQuery {
  institutionType?: "agency" | "central";
  referenceId?: string;
  categoryId?: string;
  productId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateCategoryPayload {
  name: string;
  status: "active";
}

export interface CreateProductPayload {
  categoryId: string;
  name: string;
  threshold: number;
  productKind: "stock";
  requiresVariant?: boolean;
  requiresSellingPrice?: boolean;
  status: "active";
}

export interface CreateVariantPayload {
  productId: string;
  name: string;
  unit?: string;
  sellingPrice?: number;
  status: "active";
}

export interface CentralAddStockVariantPayload {
  variantId: string;
  quantity: number;
}

export interface CentralAddStockProductPayload {
  productId: string;
  quantity?: number;
  variants?: CentralAddStockVariantPayload[];
}

export interface CentralAddStockItemPayload {
  categoryId: string;
  products: CentralAddStockProductPayload[];
}

export interface CentralAddStockPayload {
  note: string;
  date: string;
  productReference?: string;
  items: CentralAddStockItemPayload[];
}

export interface UploadedFileResult {
  url: string;
  originalImage?: string;
  fileName?: string;
}

export interface AgencyInventoryTransactionPayload {
  agencyId: string;
  variantId: string;
  quantity: number;
  transactionType: string;
  note: string;
}

export interface AgentInventoryTransactionVariantPayload {
  variantId: string;
  quantity: number;
  costPrice?: number;
}

export interface AgentInventoryTransactionProductPayload {
  productId: string;
  variants: AgentInventoryTransactionVariantPayload[];
}

export interface AgentInventoryTransactionItemPayload {
  categoryId: string;
  products: AgentInventoryTransactionProductPayload[];
}

export interface AgentInventoryTransactionPayload {
  agencyId: string;
  agentAuthUserId: string;
  posId?: string;
  taskId?: string;
  note: string;
  transactionType: string;
  items: AgentInventoryTransactionItemPayload[];
}

export interface UpdateAgencyStockVariantPayload {
  quantity: number;
}

export interface UpdatePosStockVariantPayload {
  quantity: number;
  note?: string;
}

export interface UpdateProspectStockVariantPayload {
  quantity: number;
  note?: string;
}

export interface UpdateAgentStockVariantPayload {
  quantity: number;
}

export interface CentralBankingNotePayload {
  bankingNote: string;
  date: string;
}

export interface UserEmailExistsResult {
  exists: boolean;
  message: string;
}

export interface BtlUserUpdatePayload {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  preferredLanguage?: string;
  employeeCode?: string;
  roleId?: string;
  contact?: { email?: string; phone?: AuthUserPhone };
  address?: UserAddressPayload;
  assignedSupervisorId?: string | null;
  assignedAgencyId?: string | null;
  assignedPosIds?: string[];
  accountStatus?: string;
  documents?: {
    documentType?: string;
    documentNumber?: string;
    fileName?: string;
    fileUrl?: string;
  }[];
  profile?: {
    btl?: { employeeCode?: string; designation?: string; joinedAt?: string };
  };
}

export interface maintenanceActionPayload {
  decision: string;
  reviewerNotes?: string;
}

export interface fuelActionPayload {
  decision: string;
  reviewerNotes?: string;
}

export interface LogisticsTaskReviewPayload {
  decision: "approve" | "reject";
  reviewerNotes?: string;
}

export interface LogisticsTaskReviewResult {
  id: string;
  status?: string;
  approval?: { status?: string };
}

export interface TargetListQuery {
  page?: number;
  limit?: number;
  search?: string;
  userType?: 'AGENT' | 'BTL';
  targetUserId?: string;
  agencyId?: string;
  type?: string;
  period?: string;
  status?: string;
}

export interface TargetPerformanceAnalyticsQuery {
  date: string;
  page?: number;
  limit?: number;
  userType: 'AGENT' | 'BTL';
  search?: string;
  agencyId?: string;
  userId?: string;
  targetType?: string;
  categoryId?: string;
  productId?: string;
}

export type TargetPerformanceAnalyticsDocumentType = 'excel' | 'pdf';

export interface TargetPerformanceAnalyticsDocumentQuery extends TargetPerformanceAnalyticsQuery {
  documentType: TargetPerformanceAnalyticsDocumentType;
}

export interface AgentTargetPayload {
  targetUserId: string;
  userBucket: 'AGENT' | 'BTL';
  agencyId?: string;
  type?: string;
  period?: string;
  targetValue?: number;
  dailyTarget?: number;
  weeklyTarget?: number;
  monthlyTarget?: number;
  categoryId?: string;
  productId?: string;
  targetSummary?: {
    targetType: string;
    categoryId: string | null;
    productId: string | null;
    dailyTarget: number;
    weeklyTarget: number;
    monthlyTarget: number;
  }[];
  startDate?: string;
  endDate?: string;
  notes?: string;
}

/**
 * API NORMALIZATION:
 * This abstract repository is the single contract that feature code depends on. Concrete
 * implementations can talk to the live backend or seeded mock data, but both must return the
 * same frontend-facing shapes so components and stores stay transport-agnostic.
 */
export abstract class ApiRepository {
  /**
   * Authentication and self-service profile flows.
   * These methods own session establishment, profile hydration, preferences, and password-reset
   * mechanics used by the shell and auth screens.
   */
  abstract login(payload: AuthCredentials): Promise<AuthUser | null>;
  abstract logout(sessionId: string): Promise<void>;
  abstract getProfile(): Promise<AuthUser>;
  abstract updateProfile(payload: UpdateProfilePayload): Promise<AuthUser>;
  abstract updatePreferences(payload: UpdatePreferencesPayload): Promise<void>;
  abstract changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void>;
  abstract requestSignup(
    payload: SupervisorSignupRequestPayload,
  ): Promise<void>;
  abstract requestPasswordOtp(email: string): Promise<boolean>;
  abstract verifyPasswordOtp(email: string, otp: string): Promise<boolean>;
  abstract resetPassword(email: string, nextPassword: string): Promise<boolean>;
  abstract checkUserEmailExists(email: string): Promise<UserEmailExistsResult>;

  /**
   * Agency and agent network management.
   * These contracts power directory screens plus create/edit flows that attach supervisors, agents,
   * POS records, and stock workflows to the correct organization nodes.
   */
  abstract getAgencies(
    query?: AgencyListQuery,
  ): Promise<PagedResult<AgencyRecord>>;
  abstract getAgencyProfiles(query?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PagedResult<AgencyProfileConfiguration>>;
  abstract getAgencyProfileById(
    id: string,
  ): Promise<AgencyProfileConfiguration | null>;
  abstract createAgencyProfile(payload: {
    name: string;
  }): Promise<AgencyProfileConfiguration>;
  abstract updateAgencyProfile(
    id: string,
    payload: { name: string },
  ): Promise<AgencyProfileConfiguration>;
  abstract deleteAgencyProfile(id: string): Promise<void>;
  abstract getMyAgency(): Promise<AgencyRecord | null>;
  abstract getAgencyById(id: string): Promise<AgencyRecord | null>;

  abstract createAgency(payload: AgencyUpsert): Promise<AgencyRecord>;
  abstract updateAgency(
    id: string,
    payload: AgencyUpsert,
  ): Promise<AgencyRecord | null>;

  abstract getAgents(query?: AgentListQuery): Promise<PagedResult<AgentRecord>>;

  abstract getAgentById(id: string): Promise<AgentRecord | null>;
  abstract getPendingAgentRequests(): Promise<AgentApprovalRequestRecord[]>;
  abstract approveAgentRequest(id: string): Promise<AgentRecord | null>;

  /**
   * POS and prospect lifecycle plus stock transaction history.
   * The stock reads and writes stay on this repository boundary so screens can reason in terms of
   * grouped record models instead of raw transport payloads.
   */
  abstract getPos(query?: PosListQuery): Promise<PagedResult<PosRecord>>;
  abstract getPosById(id: string): Promise<PosRecord | null>;
  abstract createPos(payload: PosUpsertPayload): Promise<PosRecord>;
  abstract updatePos(
    id: string,
    payload: PosUpsertPayload,
  ): Promise<PosRecord | null>;
  abstract deletePos(id: string): Promise<boolean>;
  abstract addPosStockTransactions(
    id: string,
    payload: PosStockTransactionPayload,
  ): Promise<boolean>;
  abstract updatePosStockVariant(
    id: string,
    variantId: string,
    payload: UpdatePosStockVariantPayload,
  ): Promise<boolean>;
  abstract deletePosStockCategory(
    id: string,
    categoryId: string,
  ): Promise<boolean>;
  abstract getPosStockDetails(id: string): Promise<PosStockDetails>;
  abstract getPosStockTransactions(id: string): Promise<PosStockEntry[]>;
  abstract getProspects(
    query?: ProspectListQuery,
  ): Promise<PagedResult<ProspectRecord>>;
  abstract getProspectById(id: string): Promise<ProspectRecord | null>;
  abstract createProspect(
    payload: ProspectUpsertPayload,
  ): Promise<ProspectRecord>;
  abstract updateProspect(
    id: string,
    payload: ProspectUpsertPayload,
  ): Promise<ProspectRecord | null>;
  abstract approveProspect(
    id: string,
    payload: ProspectApprovalPayload,
  ): Promise<ProspectRecord | null>;
  abstract deleteProspect(id: string): Promise<boolean>;
  abstract addProspectStockTransactions(
    id: string,
    payload: ProspectStockTransactionPayload,
  ): Promise<boolean>;
  abstract updateProspectStockVariant(
    id: string,
    variantId: string,
    payload: UpdateProspectStockVariantPayload,
  ): Promise<boolean>;
  abstract deleteProspectStockCategory(
    id: string,
    categoryId: string,
  ): Promise<boolean>;
  abstract getProspectStockDetails(id: string): Promise<PosStockDetails>;
  abstract getProspectStockTransactions(id: string): Promise<PosStockEntry[]>;

  /**
   * Supervisor approval and maintenance of the management hierarchy.
   */
  abstract getSupervisors(query?: {
    page?: number;
    limit?: number;
    search?: string;
    accountStatus?: string;
    approvalStatus?: string;
    agency?: string;
  }): Promise<PagedResult<SupervisorRecord>>;

  abstract getPendingSupervisorRequests(query?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PagedResult<SupervisorApprovalRequestRecord>>;
  abstract createSupervisor(
    payload: SupervisorUpsertPayload,
  ): Promise<SupervisorRecord>;
  abstract updateSupervisor(
    id: string,
    payload: SupervisorUpsertPayload,
  ): Promise<SupervisorRecord | null>;
  abstract approveSupervisorRequest(
    id: string,
    assignedAgencyId: string,
  ): Promise<SupervisorRecord | null>;
  abstract rejectSupervisorRequest(id: string): Promise<void>;
  abstract deleteSupervisor(id: string[]): Promise<boolean>;

  /**
   * Legacy agent-task workflow used by older task-management screens.
   */
  abstract getTasks(query?: TaskListQuery): Promise<PagedResult<TaskRecord>>;
  abstract getTaskById(id: string): Promise<TaskRecord | null>;
  abstract getTaskActivities(
    query?: TaskActivityListQuery,
  ): Promise<PagedResult<TaskActivity>>;
  abstract createTask(payload: CreateTaskPayload): Promise<TaskRecord>;
  abstract updateTask(
    id: string,
    payload: UpdateTaskPayload,
  ): Promise<TaskRecord>;
  abstract deleteTask(id: string): Promise<void>;
  abstract reviewAgentTask(
    taskId: string,
    payload: AgentTaskReviewPayload,
  ): Promise<AgentTaskReviewResult | null>;
  abstract getTargets(query?: TargetListQuery): Promise<PagedResult<AgentTargetRecord>>;
  abstract getTargetPerformanceAnalytics(query: TargetPerformanceAnalyticsQuery): Promise<AgentTargetAnalyticsResult>;
  abstract exportTargetPerformanceAnalyticsDocument(query: TargetPerformanceAnalyticsDocumentQuery): Promise<TargetPerformanceAnalyticsDocumentExport>;
  abstract createTarget(payload: AgentTargetPayload): Promise<AgentTargetRecord>;
  abstract updateTarget(id: string, payload: AgentTargetPayload): Promise<AgentTargetRecord>;
  abstract deleteTarget(id: string): Promise<void>;

  abstract deleteCategory(id: string): Promise<void>;

  /**
   * Inventory domain contracts.
   * BUSINESS LOGIC:
   * This section intentionally groups both central and agency/agent inventory APIs because the UI
   * reuses shared selectors, stock cards, and threshold logic across those scopes.
   */
  abstract getInventoryCategories(
    search?: string,
  ): Promise<InventoryCategory[]>;
  abstract getDefaultCentralReference(): Promise<CentralReference>;
  abstract getCashBalances(query?: CashBalanceListQuery): Promise<PagedResult<CashBalance>>;
  abstract getCashBalance(ownerType: CashOwnerType, ownerId: string): Promise<CashBalance | null>;
  abstract getCashMovements(query?: CashMovementListQuery): Promise<PagedResult<CashMovement>>;
  abstract createCashMovement(payload: CashMovementPayload): Promise<CashMovement>;
  abstract getAgentAssignedStock(
    agentId: string,
  ): Promise<AgentInventoryStockCategoryGroup[]>;
  abstract getAgentStockTransactions(
    agentId: string,
  ): Promise<AgentInventoryTransactionRecord[]>;
  abstract addAgentStockTransaction(
    payload: AgentInventoryTransactionPayload,
  ): Promise<boolean>;
  abstract updateAgentStockVariant(
    agencyId: string,
    agentAuthUserId: string,
    variantId: string,
    payload: UpdateAgentStockVariantPayload,
  ): Promise<boolean>;
  abstract deleteAgentStockCategory(
    agentId: string,
    categoryId: string,
  ): Promise<boolean>;
  abstract getAgencyAssignedStock(
    agencyId: string,
  ): Promise<AgencyInventoryStockCategoryGroup[]>;
  abstract getAgencyStockTransactions(
    agencyId: string,
  ): Promise<AgencyInventoryTransactionRecord[]>;
  abstract getAgencyDirectSalesLogs(query: AgencyDirectSalesLogQuery): Promise<PagedResult<AgencyDirectSalesLogRecord>>;
  abstract updateAgencyStockVariant(
    agencyId: string,
    variantId: string,
    payload: UpdateAgencyStockVariantPayload,
  ): Promise<boolean>;
  abstract deleteAgencyStockCategory(
    agencyId: string,
    categoryId: string,
  ): Promise<boolean>;
  abstract getInventoryProducts(query?: {
    categoryId?: string;
    search?: string;
  }): Promise<InventoryProduct[]>;
  abstract getInventoryVariants(query?: {
    productId?: string;
    categoryId?: string;
    search?: string;
  }): Promise<InventoryVariant[]>;
  abstract getInventoryCategoriesPaged(query?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PagedResult<InventoryCategory>>;
  abstract getInventoryProductsPaged(query?: {
    categoryId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PagedResult<InventoryProduct>>;
  abstract getInventoryVariantsPaged(query?: {
    productId?: string;
    categoryId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PagedResult<InventoryVariant>>;
  abstract exportInventoryCatalogExcel(): Promise<InventoryCatalogExcelExport>;
  abstract getCentralStockOverview(filters?: {
    categoryId?: string;
    productId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<CentralStockOverviewData | null>;
  abstract getCentralStockList(
    centralId?: string,
    query?: { search?: string; page?: number; limit?: number },
  ): Promise<PagedResult<CentralStockListRow>>;
  abstract getCentralTransfers(query?: {
    status?: "pending" | "in-transit" | "delivered";
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PagedResult<CentralTransferListRow>>;
  abstract getCentralBankingNotes(query?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PagedResult<CentralBankingNoteRow>>;
  abstract createCentralBankingNote(
    payload: CentralBankingNotePayload,
  ): Promise<CentralBankingNoteRow | null>;
  abstract updateCentralBankingNote(
    id: string,
    payload: CentralBankingNotePayload,
  ): Promise<CentralBankingNoteRow | null>;
  abstract deleteCentralBankingNote(id: string): Promise<boolean>;
  abstract getAgencyReferences(
    search?: string,
  ): Promise<AgencyReferenceOption[]>;
  abstract getStockThresholds(
    query?: StockThresholdQuery,
  ): Promise<PagedResult<StockThresholdRow>>;
  abstract createStockThreshold(
    payload: CreateStockThresholdPayload,
  ): Promise<StockThresholdRow | null>;
  abstract updateStockThreshold(
    id: string,
    payload: CreateStockThresholdPayload,
  ): Promise<StockThresholdRow | null>;
  abstract deleteStockThreshold(id: string): Promise<boolean>;
  abstract uploadFile(file: File, context?: string): Promise<UploadedFileResult>;
  abstract addCentralStock(payload: CentralAddStockPayload): Promise<boolean>;
  abstract addAgencyStockTransaction(
    payload: AgencyInventoryTransactionPayload,
  ): Promise<boolean>;
  abstract createCategory(
    payload: CreateCategoryPayload,
  ): Promise<InventoryCategory>;
  abstract updateCategory(
    id: string,
    payload: CreateCategoryPayload,
  ): Promise<InventoryCategory>;
  abstract createProduct(
    payload: CreateProductPayload,
  ): Promise<InventoryProduct>;
  abstract updateProduct(
    id: string,
    payload: CreateProductPayload,
  ): Promise<InventoryProduct>;
  abstract deleteProduct(id: string): Promise<void>;
  abstract createVariant(
    payload: CreateVariantPayload,
  ): Promise<InventoryVariant>;
  abstract updateVariant(
    id: string,
    payload: CreateVariantPayload,
  ): Promise<InventoryVariant>;
  abstract deleteVariant(id: string): Promise<void>;

  /**
   * BTL and logistics operational workflows.
   * These methods cover field users, campaigns, route tasks, vehicle documents, and approval-style
   * operational requests that feed multiple dashboard and workflow screens.
   */
  abstract getBtlUsers(query?: {
    page?: number;
    limit?: number;
    search?: string;
    accountStatus?: string;
    approvalStatus?: string;
    agency?: string;
    agencyId?: string;
  }): Promise<PagedResult<BtlUserApiRecord>>;
  abstract getBtlUserById(id: string): Promise<BtlUserApiRecord | null>;
  abstract getBtlCampaigns(
    query?: BtlCampaignListQuery,
  ): Promise<PagedResult<BtlCampaignListItem>>;
  abstract getBtlCampaignById(id: string): Promise<BtlCampaignApiRecord | null>;
  abstract getFuelById(id: string): Promise<FuelRequestApiRecord | null>;
  abstract getVehicleById(id: string): Promise<VehicleApiRecord | null>;

  abstract getBtlDashboardSummary(
    agencyId?: string,
  ): Promise<BtlDashboardSummary>;
  abstract createBtlCampaign(
    payload: BtlCampaignUpsertPayload,
  ): Promise<BtlCampaignApiRecord | null>;
  abstract updateBtlCampaign(
    id: string,
    payload: BtlCampaignUpsertPayload,
  ): Promise<BtlCampaignApiRecord | null>;
  abstract deleteBtlCampaign(id: string): Promise<boolean>;
  abstract getLogisticsDashboardSummary(
    agencyId?: string,
  ): Promise<LogisticsDashboardSummary>;
  abstract getLogisticsTasks(
    query?: LogisticsTaskListQuery,
  ): Promise<PagedResult<LogisticsTaskListItem>>;
  abstract getLogisticsTaskById(
    id: string,
  ): Promise<LogisticsTaskDetailApiRecord | null>;
  abstract createLogisticsTask(
    payload: LogisticsTaskUpsertPayload,
  ): Promise<unknown>;
  abstract updateLogisticsTask(
    id: string,
    payload: LogisticsTaskUpsertPayload,
  ): Promise<unknown>;
  abstract deleteLogisticsTask(id: string): Promise<void>;
  abstract getLogisticsUsers(query?: {
    page?: number;
    limit?: number;
    search?: string;
    accountStatus?: string;
    approvalStatus?: string;
    agency?: string;
    agencyId?: string;
  }): Promise<PagedResult<LogisticsUserApiRecord>>;
  abstract getLogisticsUserById(
    id: string,
  ): Promise<LogisticsUserApiRecord | null>;
  abstract getVehicles(query?: {
    page?: number;
    limit?: number;
    search?: string;
    assignedToAuthUserId?: string;
    status?: string;
    isActive?: string;
    type?: string;
  }): Promise<PagedResult<VehicleApiRecord>>;
  abstract createVehicle(
    payload: CreateVehiclePayload,
  ): Promise<VehicleApiRecord>;
  abstract updateVehicle(
    id: string,
    payload: UpdateVehiclePayload,
  ): Promise<VehicleApiRecord | null>;
  abstract createVehicleDocument(
    payload: CreateVehicleDocumentStandalonePayload,
  ): Promise<VehicleDocumentApiRecord | null>;
  abstract updateVehicleDocument(
    documentId: string,
    payload: UpdateVehicleDocumentPayload,
  ): Promise<VehicleDocumentApiRecord | null>;
  abstract getVehicleDocuments(
    query?: VehicleDocumentListQuery,
  ): Promise<PagedResult<VehicleDocumentApiRecord>>;
  abstract getVehicleDocumentsByVehicleId(
    vehicleId: string,
    query?: { page?: number; limit?: number },
  ): Promise<PagedResult<VehicleDocumentApiRecord>>;
  abstract deleteVehicleDocument(documentId: string): Promise<boolean>;
  abstract getFuelRequests(
    query?: FuelRequestListQuery,
  ): Promise<PagedResult<FuelRequestApiRecord>>;
  abstract updateBtlUser(
    id: string,
    payload: BtlUserUpdatePayload,
  ): Promise<BtlUserApiRecord | null>;

  abstract getMaintenanceTypes(query?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PagedResult<MaintenanceTypeRecord>>;
  abstract getMaintenanceTypeById(
    id: string,
  ): Promise<MaintenanceTypeRecord | null>;
  abstract createMaintenanceType(payload: {
    name: string;
  }): Promise<MaintenanceTypeRecord>;
  abstract updateMaintenanceType(
    id: string,
    payload: { name: string },
  ): Promise<MaintenanceTypeRecord>;
  abstract deleteMaintenanceType(id: string): Promise<void>;

  /**
   * Access control, dashboard, and admin-user management.
   * The dashboard summary methods expose pre-aggregated backend data, while the user-management
   * methods keep CRUD and password flows behind one consistent contract.
   */
  abstract getUsers(params?: UsersListParams): Promise<UsersListResult>;
  abstract getUserById(id: string): Promise<ApiUserSummary | null>;
  abstract getDashboardActiveSummary(query?: {
    assignedAgencyId?: string;
  }): Promise<DashboardActiveSummary>;
  abstract getDashboardSummary(query?: {
    agencyId?: string;
  }): Promise<DashboardSummary>;
  abstract getSupervisorDashboardSummary(query?: SupervisorDashboardSummaryQuery): Promise<SupervisorDashboardSummary>;
  abstract exportDashboardPdf(query?: {
    agencyId?: string;
  }): Promise<DashboardPdfExport>;
  abstract exportSupervisorDashboardPdf(): Promise<DashboardPdfExport>;
  abstract getAdminById(id: string): Promise<ApiUserSummary | null>;

  abstract createUser(payload: CreateUserPayload): Promise<ApiUserSummary>;
  abstract createAdminUser(payload: CreateUserPayload): Promise<ApiUserSummary>;
  abstract createAgent(payload: CreateUserPayload): Promise<ApiUserSummary>;
  abstract createBtlUser(payload: CreateUserPayload): Promise<ApiUserSummary>;
  abstract createLogisticsUser(
    payload: CreateUserPayload,
  ): Promise<ApiUserSummary>;
  abstract updateLogisticsUser(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary>;
  abstract updateUserProfile(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary>;
  abstract updateAdminUser(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary>;
  abstract updateAgent(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary>;
  abstract deleteAgent(ids: string[]): Promise<void>;
  abstract deleteBtlUser(ids: string[]): Promise<Boolean>;
  abstract changeAgentPassword(id: string, newPassword: string): Promise<void>;
  abstract changeLogisticsPassword(
    id: string,
    newPassword: string,
  ): Promise<void>;
  abstract changeBtlPassword(id: string, newPassword: string): Promise<void>;
  abstract deleteAgency(id: string): Promise<void>;

  abstract changeAdminPassword(id: string, newPassword: string): Promise<void>;
  abstract changeSupervisorPassword(
    id: string,
    newPassword: string,
  ): Promise<void>;
  abstract updateUserStatus(
    id: string,
    payload: UpdateUserStatusPayload,
  ): Promise<ApiUserSummary>;

  abstract deleteLogisticUser(id: string[]): Promise<boolean>;
  abstract deleteFuel(id: string[]): Promise<boolean>;
  abstract deleteVehicle(id: string[]): Promise<boolean>;
  abstract deleteAdmin(id: string[]): Promise<boolean>;

  /**
   * Review actions for logistics approvals.
   * BUSINESS LOGIC:
   * These are separated from generic updates because the UI treats approve/reject as explicit admin
   * review flows with their own confirmation state and audit-note handling.
   */
  abstract approveMaintenance(
    vehicleId: string,
    maintenanceRequestId: string,
    payload: maintenanceActionPayload,
  ): Promise<MaintenanceRequestApiRecord | null>;
  abstract actionFuel(
    id: string,
    payload: fuelActionPayload,
  ): Promise<FuelRequestApiRecord | null>;
  abstract reviewLogisticsTask(
    taskId: string,
    payload: LogisticsTaskReviewPayload,
  ): Promise<LogisticsTaskReviewResult | null>;

  /**
   * Fuel and maintenance request CRUD.
   * These power the dedicated logistics forms as well as the admin review lists.
   */
  abstract createFuelRequest(
    payload: CreateFuelRequestPayload,
  ): Promise<FuelRequestApiRecord | null>;
  abstract updateFuelRequest(
    id: string,
    payload: UpdateFuelRequestPayload,
  ): Promise<FuelRequestApiRecord | null>;

  abstract getMaintenanceRequests(
    query?: MaintenanceRequestListQuery,
  ): Promise<PagedResult<MaintenanceRequestApiRecord>>;
  abstract getMaintenanceRequestById(
    vehicleId: string,
    maintenanceRequestId: string,
  ): Promise<MaintenanceRequestApiRecord | null>;
  abstract deleteMaintenanceRequest(
    vehicleId: string,
    maintenanceRequestId: string,
  ): Promise<boolean>;
  abstract createMaintenanceRequest(
    payload: CreateMaintenanceRequestPayload,
  ): Promise<MaintenanceRequestApiRecord | null>;
  abstract updateMaintenanceRequest(
    vehicleId: string,
    maintenanceRequestId: string,
    payload: UpdateMaintenanceRequestPayload,
  ): Promise<MaintenanceRequestApiRecord | null>;
}
